/**
 * Delegation API Utility
 *
 * API functions for delegation and invitation management.
 * Used by the Settings > Delegates tab for:
 * - Creating and revoking invitations
 * - Listing sent invitations and granted delegations
 * - Revoking active delegations
 */

import api from './api';

export interface SentInvitation {
  invitationToken: string;
  inviteeEmail: string;
  inviteeName?: string;
  delegatorUserId: string;
  delegatorAliId: string;
  delegatorName: string;
  collectionId: string;
  collectionName: string;
  /**
   * Catalog-scope narrowing for a catalog-wide ("all") grant (GH#1219).
   * Empty/undefined = all the delegator's catalogs; an LL-NXX narrows to
   * that one owned catalog.
   */
  catalogAliId?: string;
  permissions: string;
  /** Granular CV-edit capability, decoupled from view/full item access. */
  canEditCv?: boolean;
  status: string;
  message?: string;
  createdAt: string;
  expiresAt: string;
}

export interface ActiveDelegation {
  delegationKey: string;
  delegatorAliId: string;
  delegatorUserId: string;
  delegateUserId: string;
  delegateEmail: string;
  delegateAliId?: string;
  collectionId: string;
  /** Catalog-scope narrowing for a catalog-wide grant (GH#1219). */
  catalogAliId?: string;
  permissions: string;
  /** Granular CV-edit capability, decoupled from view/full item access. */
  canEditCv?: boolean;
  createdAt: string;
  expiresAt: string;
  assignedBy: string;
  isDeleted: boolean;
}

export interface CreateInvitationRequest {
  inviteeEmail: string;
  inviteeName?: string;
  collectionId: string;
  /**
   * Catalog-scope narrowing — only valid with collectionId === 'all'
   * (GH#1219). An LL-NXX narrows the catalog-wide grant to that one
   * owned catalog.
   */
  catalogAliId?: string;
  permissions: string;
  /** Grant CV-edit capability at invite time. */
  canEditCv?: boolean;
  expiresInDays: number;
  message?: string;
}

interface InvitationListResponse {
  invitations: SentInvitation[];
  count: number;
}

interface DelegationListResponse {
  delegations: ActiveDelegation[];
  count: number;
}

interface CreateInvitationResponse {
  success: boolean;
  invitationToken: string;
}

interface SuccessResponse {
  success: boolean;
}

export interface UpdateDelegationRequest {
  expiresAt?: string;
  permissions?: string;
  /**
   * New collection/catalog scope (a collection ID or the "all" sentinel).
   * collectionId is part of the delegation's key, so changing it re-scopes
   * the delegation server-side (revoke old + recreate at the new collection).
   */
  collectionId?: string;
  /**
   * Catalog-scope narrowing for a catalog-wide ("all") grant (GH#1219).
   * Only valid with collectionId === 'all'.
   */
  catalogAliId?: string;
  /** Toggle the granular CV-edit capability on/off. */
  canEditCv?: boolean;
}

export async function createDelegationInvitation(
  data: CreateInvitationRequest
): Promise<CreateInvitationResponse> {
  const response = await api.post('/api/delegation-invitations', data as unknown as Record<string, unknown>);
  return response.data as CreateInvitationResponse;
}

export async function fetchSentInvitations(): Promise<InvitationListResponse> {
  const response = await api.get('/api/delegation-invitations/sent');
  return response.data as InvitationListResponse;
}

export async function revokeInvitation(token: string): Promise<SuccessResponse> {
  const response = await api.delete(`/api/delegation-invitations/${token}`);
  return response.data as SuccessResponse;
}

export async function fetchGrantedDelegations(): Promise<DelegationListResponse> {
  const response = await api.get('/api/delegations/granted');
  return response.data as DelegationListResponse;
}

/**
 * Revoke (soft-delete) a delegation.
 *
 * The delegation ID contains '#' characters (format: delegatorAliId#DELEGATE#userId#collectionId)
 * which MUST be URL-encoded to prevent the browser from treating them as fragment identifiers.
 */
export async function revokeDelegation(delegationId: string): Promise<SuccessResponse> {
  const response = await api.delete(`/api/delegations/${encodeURIComponent(delegationId)}`);
  return response.data as SuccessResponse;
}

/**
 * Update a delegation's expiration date or permissions.
 *
 * The delegation ID contains '#' characters (format: delegatorAliId#DELEGATE#userId#collectionId)
 * which MUST be URL-encoded to prevent the browser from treating them as fragment identifiers.
 */
export async function updateDelegation(
  delegationId: string,
  data: UpdateDelegationRequest
): Promise<SuccessResponse> {
  const response = await api.put(
    `/api/delegations/${encodeURIComponent(delegationId)}`,
    data as unknown as Record<string, unknown>
  );
  return response.data as SuccessResponse;
}
